import React, { useState, useEffect } from 'react';
import { adminAPI } from '../../utils/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { 
  ClipboardList, 
  Search, 
  Loader2,
  Calendar,
  User,
  ArrowRight,
  Filter,
  RefreshCw,
  ChevronLeft,
  ChevronRight,
  ExternalLink,
  Clock,
  FileText
} from 'lucide-react';

const ROLES = {
  admin: { label: 'Admin', color: 'bg-red-100 text-red-800 border-red-200' },
  sales: { label: 'Sales', color: 'bg-blue-100 text-blue-800 border-blue-200' },
  production: { label: 'Production', color: 'bg-orange-100 text-orange-800 border-orange-200' },
  front_desk: { label: 'Front Desk', color: 'bg-purple-100 text-purple-800 border-purple-200' },
  user: { label: 'User', color: 'bg-gray-100 text-gray-800 border-gray-200' },
  customer: { label: 'Customer', color: 'bg-green-100 text-green-800 border-green-200' },
};

const DATE_RANGE_OPTIONS = [
  { value: '7', label: 'Last 7 days' },
  { value: '30', label: 'Last 30 days' },
  { value: '90', label: 'Last 90 days' },
  { value: 'all', label: 'All time' },
];

export default function AdminRoleAuditLog({ onNavigateToUser }) {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [limit] = useState(20);
  
  // Filters
  const [targetUserSearch, setTargetUserSearch] = useState('');
  const [changedBySearch, setChangedBySearch] = useState('');
  const [dateRange, setDateRange] = useState('30');
  const [users, setUsers] = useState([]);
  const [selectedTargetUser, setSelectedTargetUser] = useState('');
  const [selectedChangedBy, setSelectedChangedBy] = useState('');

  useEffect(() => {
    fetchAuditLogs();
    fetchStats();
    fetchUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, dateRange, selectedTargetUser, selectedChangedBy]);

  const fetchUsers = async () => {
    try {
      const data = await adminAPI.getUsers({ limit: 100 });
      setUsers(data.users || []);
    } catch (err) {
      console.error('Failed to fetch users:', err);
    }
  };

  const fetchStats = async () => {
    try {
      const data = await adminAPI.getRoleAuditStats();
      setStats(data);
    } catch (err) {
      console.error('Failed to fetch audit stats:', err);
    }
  };

  const fetchAuditLogs = async () => {
    try {
      setLoading(true);
      const params = {
        limit,
        skip: (page - 1) * limit,
      };
      
      if (dateRange !== 'all') {
        params.days = parseInt(dateRange);
      }
      
      if (selectedTargetUser) {
        params.target_user_id = selectedTargetUser;
      }
      
      if (selectedChangedBy) {
        params.changed_by_user_id = selectedChangedBy;
      }
      
      const data = await adminAPI.getRoleAuditLogs(params);
      setLogs(data.logs || []);
      setTotal(data.total || 0);
    } catch (err) {
      console.error('Failed to fetch audit logs:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = () => {
    fetchAuditLogs();
    fetchStats();
  };

  const clearFilters = () => {
    setSelectedTargetUser('');
    setSelectedChangedBy('');
    setDateRange('30');
    setPage(1);
  };

  const getRoleStyle = (role) => {
    return ROLES[role]?.color || 'bg-gray-100 text-gray-800 border-gray-200';
  };

  const getRoleLabel = (role) => {
    return ROLES[role]?.label || role;
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatRelativeTime = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return formatDate(dateString);
  };

  const totalPages = Math.ceil(total / limit);

  // Filter users for dropdown based on search
  const filteredTargetUsers = users.filter(u => 
    !targetUserSearch || 
    u.name?.toLowerCase().includes(targetUserSearch.toLowerCase()) ||
    u.email?.toLowerCase().includes(targetUserSearch.toLowerCase())
  );

  const filteredChangedByUsers = users.filter(u => 
    !changedBySearch || 
    u.name?.toLowerCase().includes(changedBySearch.toLowerCase()) ||
    u.email?.toLowerCase().includes(changedBySearch.toLowerCase())
  );

  return (
    <div className="p-6" data-testid="admin-role-audit-log-page">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Role Change Audit Log</h1>
          <p className="text-gray-600">Track all changes to user roles</p>
        </div>
        <Button 
          variant="outline" 
          onClick={handleRefresh}
          data-testid="refresh-audit-logs-btn"
        >
          <RefreshCw className="w-4 h-4 mr-2" />
          Refresh
        </Button>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-white rounded-lg border p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                <ClipboardList className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">{stats.total_changes}</p>
                <p className="text-xs text-gray-500">Total Changes</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-lg border p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                <Calendar className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">{stats.last_7_days}</p>
                <p className="text-xs text-gray-500">Last 7 Days</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-lg border p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                <Clock className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">{stats.last_30_days}</p>
                <p className="text-xs text-gray-500">Last 30 Days</p>
              </div>
            </div>
          </div>
          {stats.most_recent && (
            <div className="bg-white rounded-lg border p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center">
                  <User className="w-5 h-5 text-orange-600" />
                </div>
                <div className="truncate">
                  <p className="text-sm font-medium text-gray-900 truncate">
                    {stats.most_recent.target_user_name || stats.most_recent.target_user_email}
                  </p>
                  <p className="text-xs text-gray-500">
                    {formatRelativeTime(stats.most_recent.created_at)}
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Filters */}
      <div className="bg-white rounded-lg shadow-sm border p-4 mb-6">
        <div className="flex items-center gap-2 mb-3">
          <Filter className="w-4 h-4 text-gray-500" />
          <span className="font-medium text-gray-700">Filters</span>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {/* Target User Filter */}
          <div>
            <label className="block text-xs text-gray-500 mb-1">Target User</label>
            <Select 
              value={selectedTargetUser || 'all'} 
              onValueChange={(val) => { setSelectedTargetUser(val === 'all' ? '' : val); setPage(1); }}
            >
              <SelectTrigger data-testid="filter-target-user">
                <SelectValue placeholder="All users" />
              </SelectTrigger>
              <SelectContent>
                <div className="p-2">
                  <Input
                    placeholder="Search users..."
                    value={targetUserSearch}
                    onChange={(e) => setTargetUserSearch(e.target.value)}
                    className="mb-2"
                  />
                </div>
                <SelectItem value="all">All users</SelectItem>
                {filteredTargetUsers.slice(0, 20).map(user => (
                  <SelectItem key={user.user_id} value={user.user_id}>
                    {user.name || user.email}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Changed By Filter */}
          <div>
            <label className="block text-xs text-gray-500 mb-1">Changed By</label>
            <Select 
              value={selectedChangedBy || 'all'} 
              onValueChange={(val) => { setSelectedChangedBy(val === 'all' ? '' : val); setPage(1); }}
            >
              <SelectTrigger data-testid="filter-changed-by">
                <SelectValue placeholder="Anyone" />
              </SelectTrigger>
              <SelectContent>
                <div className="p-2">
                  <Input
                    placeholder="Search users..."
                    value={changedBySearch}
                    onChange={(e) => setChangedBySearch(e.target.value)}
                    className="mb-2"
                  />
                </div>
                <SelectItem value="all">Anyone</SelectItem>
                {filteredChangedByUsers.slice(0, 20).map(user => (
                  <SelectItem key={user.user_id} value={user.user_id}>
                    {user.name || user.email}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Date Range Filter */}
          <div>
            <label className="block text-xs text-gray-500 mb-1">Date Range</label>
            <Select 
              value={dateRange} 
              onValueChange={(val) => { setDateRange(val); setPage(1); }}
            >
              <SelectTrigger data-testid="filter-date-range">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {DATE_RANGE_OPTIONS.map(option => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Clear Filters */}
          <div className="flex items-end">
            <Button 
              variant="ghost" 
              onClick={clearFilters}
              className="w-full"
              data-testid="clear-filters-btn"
            >
              Clear Filters
            </Button>
          </div>
        </div>
      </div>

      {/* Audit Log Table */}
      <div className="bg-white rounded-lg shadow-sm border overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full" data-testid="audit-log-table">
            <thead className="bg-gray-50 border-b">
              <tr>
                <th className="text-left py-3 px-4 font-medium text-gray-700">Timestamp</th>
                <th className="text-left py-3 px-4 font-medium text-gray-700">Target User</th>
                <th className="text-left py-3 px-4 font-medium text-gray-700">Role Change</th>
                <th className="text-left py-3 px-4 font-medium text-gray-700">Changed By</th>
                <th className="text-left py-3 px-4 font-medium text-gray-700">Reason</th>
                <th className="text-left py-3 px-4 font-medium text-gray-700">Source</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="6" className="text-center py-12">
                    <Loader2 className="w-6 h-6 animate-spin mx-auto text-gray-400" />
                    <p className="text-gray-500 mt-2">Loading audit logs...</p>
                  </td>
                </tr>
              ) : logs.length === 0 ? (
                <tr>
                  <td colSpan="6" className="text-center py-12">
                    <ClipboardList className="w-10 h-10 mx-auto text-gray-300 mb-3" />
                    <p className="text-gray-500">No audit logs found</p>
                    <p className="text-gray-400 text-sm mt-1">
                      Role changes will appear here when users update roles
                    </p>
                  </td>
                </tr>
              ) : (
                logs.map((log) => (
                  <tr 
                    key={log.id} 
                    className="border-b hover:bg-gray-50 transition-colors"
                    data-testid={`audit-log-row-${log.id}`}
                  >
                    <td className="py-3 px-4">
                      <div className="flex flex-col">
                        <span className="text-sm text-gray-900">{formatDate(log.created_at)}</span>
                        <span className="text-xs text-gray-400">{formatRelativeTime(log.created_at)}</span>
                      </div>
                    </td>
                    <td className="py-3 px-4">
                      <div 
                        className="flex items-center gap-2 cursor-pointer hover:text-blue-600"
                        onClick={() => onNavigateToUser && onNavigateToUser(log.target_user_id)}
                      >
                        <div className="w-8 h-8 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-full flex items-center justify-center">
                          <span className="text-white font-semibold text-xs">
                            {log.target_user_name?.charAt(0)?.toUpperCase() || log.target_user_email?.charAt(0)?.toUpperCase() || '?'}
                          </span>
                        </div>
                        <div>
                          <p className="text-sm font-medium">{log.target_user_name || 'Unnamed'}</p>
                          <p className="text-xs text-gray-400">{log.target_user_email}</p>
                        </div>
                        {onNavigateToUser && (
                          <ExternalLink className="w-3 h-3 text-gray-400" />
                        )}
                      </div>
                    </td>
                    <td className="py-3 px-4">
                      <div className="flex items-center gap-2">
                        <div className="flex flex-wrap gap-1">
                          {(log.old_roles || [log.old_role]).map(role => (
                            <Badge 
                              key={role} 
                              className={`${getRoleStyle(role)} border text-xs`}
                            >
                              {getRoleLabel(role)}
                            </Badge>
                          ))}
                        </div>
                        <ArrowRight className="w-4 h-4 text-gray-400 flex-shrink-0" />
                        <div className="flex flex-wrap gap-1">
                          {(log.new_roles || [log.new_role]).map(role => (
                            <Badge 
                              key={role} 
                              className={`${getRoleStyle(role)} border text-xs`}
                            >
                              {getRoleLabel(role)}
                            </Badge>
                          ))}
                        </div>
                      </div>
                    </td>
                    <td className="py-3 px-4">
                      <div className="flex items-center gap-2">
                        <div className="w-6 h-6 bg-gray-200 rounded-full flex items-center justify-center">
                          <span className="text-gray-600 font-semibold text-xs">
                            {log.changed_by_name?.charAt(0)?.toUpperCase() || log.changed_by_email?.charAt(0)?.toUpperCase() || '?'}
                          </span>
                        </div>
                        <div>
                          <p className="text-sm">{log.changed_by_name || 'Unknown'}</p>
                          <p className="text-xs text-gray-400">{log.changed_by_email}</p>
                        </div>
                      </div>
                    </td>
                    <td className="py-3 px-4">
                      {log.reason ? (
                        <div className="flex items-start gap-1 max-w-xs">
                          <FileText className="w-3 h-3 text-gray-400 mt-0.5 flex-shrink-0" />
                          <span className="text-sm text-gray-600 line-clamp-2">{log.reason}</span>
                        </div>
                      ) : (
                        <span className="text-sm text-gray-400 italic">No reason provided</span>
                      )}
                    </td>
                    <td className="py-3 px-4">
                      <Badge variant="outline" className="text-xs">
                        {log.source || 'admin_ui'}
                      </Badge>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        
        {/* Table Footer with Pagination */}
        <div className="px-4 py-3 bg-gray-50 border-t flex items-center justify-between">
          <div className="text-sm text-gray-500">
            Showing {logs.length} of {total} entries
          </div>
          {totalPages > 1 && (
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                disabled={page === 1}
                onClick={() => setPage(p => Math.max(1, p - 1))}
                data-testid="prev-page-btn"
              >
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <span className="text-sm text-gray-600">
                Page {page} of {totalPages}
              </span>
              <Button
                variant="outline"
                size="sm"
                disabled={page >= totalPages}
                onClick={() => setPage(p => p + 1)}
                data-testid="next-page-btn"
              >
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          )}
        </div>
      </div>

      {/* Info Box */}
      <div className="mt-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <ClipboardList className="w-5 h-5 text-blue-500 mt-0.5" />
          <div>
            <p className="font-medium text-blue-800">About Role Audit Logs</p>
            <p className="text-sm text-blue-700 mt-1">
              This log tracks all changes to user roles in the system. Every time an admin changes a user&apos;s role, 
              an entry is created showing who made the change, what changed, and when. This helps maintain 
              accountability and compliance for access control changes.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
